import React, { useState, useRef } from "react";
import Card from "components/card";
import Modal from "components/modal/Modal";
import { MdAdd, MdDeleteOutline, MdCheckCircle, MdError, MdCancel, MdFileUpload } from "react-icons/md";
import { v4 as uuidv4 } from "uuid";
import { useDevices, Device } from "context/DeviceContext";
import { useAuth } from "context/AuthContext";
import { uploadExibicaoImage } from "services/storageService";
import { useAprendizado } from "context/AprendizadoContext";
import { useUnidades } from "context/UnidadeContext";
import { useSelectedDevice } from "context/SelectedDeviceContext";
import { useItens } from "context/ItensContext";
import { useRelatorios } from "context/RelatorioContext";

export default function Dispositivos() {
  const { user } = useAuth();
  const { devices, adicionarDevice, removerDevice, editarDevice } = useDevices();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [exibicaoOption, setExibicaoOption] = useState<'Camera' | 'Personalizado' | 'Pesquisa'>('Camera');
  const { adicionarUnidade, removerUnidade } = useUnidades();
  const { adicionarAprendizado, removerPorDispositivo } = useAprendizado();
  const { selectedDeviceId, setSelectedDeviceId } = useSelectedDevice();
  const { removerItensPorDispositivo } = useItens();
  const { relatorios, removerRelatoriosPorDispositivo } = useRelatorios();
  const [deleteMode, setDeleteMode] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);

  const [newDevice, setNewDevice] = useState<Omit<Device, "id" | "status">>({
    name: "",
    days: [],
    expedientes: {},
    token: "",
    exibicao: "",
    titulo: "",
    sobraLimpa: false,
  });

  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [tempTitulo, setTempTitulo] = useState("");

  const statusIcon = (status: string) => {
    switch (status) {
      case "Operando":
        return <MdCheckCircle className="text-green-500" />;
      case "Desligado":
        return <MdCancel className="text-red-500" />;
      case "Aguardando":
        return <MdError className="text-yellow-500" />;
      default:
        return null;
    }
  };

  const getHojeStr = () => {
    const date = new Date();
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const validarExpedientes = (expedientes: Record<string, { start: string, end: string }>) => {
    const intervals: { titulo: string, s: number, e: number }[] = [];

    for (const [titulo, exp] of Object.entries(expedientes)) {
      if (!exp.start || !exp.end) continue;
      const [startH, startM] = exp.start.split(':').map(Number);
      const [endH, endM] = exp.end.split(':').map(Number);
      const startMins = startH * 60 + startM;
      const endMins = endH * 60 + endM;

      if (startMins === endMins) {
        return `O horário de início e fim do expediente "${titulo}" não podem ser iguais.`;
      }

      // Se for madrugada, divide em dois blocos virtuais para checar sobreposição
      if (endMins < startMins) {
        intervals.push({ titulo, s: startMins, e: 1440 }); // Até a meia-noite
        intervals.push({ titulo, s: 0, e: endMins });      // Da meia-noite até o fim
      } else {
        intervals.push({ titulo, s: startMins, e: endMins });
      }
    }

    for (let i = 0; i < intervals.length; i++) {
      for (let j = i + 1; j < intervals.length; j++) {
        if (intervals[i].titulo !== intervals[j].titulo) {
          if (intervals[i].s < intervals[j].e && intervals[j].s < intervals[i].e) {
            return `Os horários dos expedientes "${intervals[i].titulo}" e "${intervals[j].titulo}" se sobrepõem.`;
          }
        }
      }
    }
    return null;
  };

  const handleAddDevice = () => {
    setNewDevice({
      name: "",
      days: [],
      expedientes: { '': { start: '', end: '' } },
      token: uuidv4().slice(0, 8),
      exibicao: "",
      titulo: "",
      sobraLimpa: false,
    });
    setShowAddModal(true);
  };

  const handleSaveAndConfirm = () => {
    handleSaveNewDevice();
    confirmAddDevice();
  };

  const handleSaveNewDevice = () => {
    const expedientesArray = Object.values(newDevice.expedientes);
    const temExpedientes = expedientesArray.length > 0;
    const expedientesValidos = expedientesArray.every(exp => exp.start && exp.end);
    const titulosValidos = Object.keys(newDevice.expedientes).every(titulo => titulo.trim() !== "");

    if (!newDevice.name.trim() || newDevice.days.length === 0 || !temExpedientes || !expedientesValidos || !titulosValidos) {
      alert("Por favor, preencha o nome, selecione os dias e certifique-se de que TODOS os expedientes tenham título, início e fim.");
      return false;
    }

    const erroValidacao = validarExpedientes(newDevice.expedientes);
    if (erroValidacao) {
      alert(erroValidacao);
      return false;
    }

    // const now = new Date();
    // const currentMinutes = now.getHours() * 60 + now.getMinutes();
    // const currentDay = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"][now.getDay()];

    // if (newDevice.days.includes(currentDay)) {
    //   for (const [titulo, exp] of Object.entries(newDevice.expedientes)) {
    //     const [endH, endM] = exp.end.split(':').map(Number);
    //     const endMinutes = endH * 60 + endM;
    //     if (endMinutes <= currentMinutes) {
    //       alert(`O horário de fim do expediente "${titulo}" já passou. Não é possível criar um expediente que termina no passado para o dia de hoje.`);
    //       return false;
    //     }
    //   }
    // }

    return true;
  };

  const confirmAddDevice = async () => {
    if (!handleSaveNewDevice()) return;

    setShowAddModal(false);
    setShowTokenModal(true);

    const hojeISO = new Date().toISOString().split("T")[0];
    const hojeBR = new Date().toLocaleDateString("pt-BR");

    try {
      const deviceId = await adicionarDevice({
        name: newDevice.name,
        status: "Desligado",
        days: newDevice.days,
        expedientes: newDevice.expedientes,
        token: newDevice.token,
        exibicao: "",
        titulo: "",
        sobraLimpa: newDevice.sobraLimpa,
      });

      await adicionarAprendizado({
        nome: newDevice.name,
        dispositivoId: deviceId,
        status: "Aprendendo",
        dataCadastro: hojeISO,
        mediaPessoasPorDia: 0,
      });

      await adicionarUnidade({
        nome: newDevice.name,
        dispositivoId: deviceId,
        data: hojeBR,
        contagemAtual: 0,
        metaMaxima: null,
      });

    } catch (e) {
      console.error("Erro adicionando dispositivo:", e);
      alert("Falha ao criar. Tente novamente!");
    }
  };

  const handleDeleteDevice = async (id: string) => {
    const confirmDelete = window.confirm(
      "Tem certeza que deseja apagar esse dispositivo?\nTODOS os dados associados (unidade, aprendizado, relatórios e itens) serão apagados permanentemente!"
    );
    if (confirmDelete) {
      try {
        await removerItensPorDispositivo(id);
        await removerRelatoriosPorDispositivo(id);
        await removerPorDispositivo(id);
        await removerUnidade(id);
        await removerDevice(id);

        setDeleteMode(false);
        if (selectedDeviceId === id) {
          setSelectedDeviceId(null);
        }
      } catch (error) {
        console.error("Erro na exclusão em cascata do dispositivo:", error);
        alert("Erro ao remover o dispositivo e seus dados. Tente novamente!");
      }
    }
  };

  const handleEditDevice = (device: Device) => {
    const expedientesCopia = JSON.parse(JSON.stringify(device.expedientes || {}));

    setEditingDevice({
      ...device,
      days: [...device.days],
      expedientes: expedientesCopia
    });

    if (device.exibicao === 'Pesquisa') {
      setExibicaoOption('Pesquisa');
      setTempTitulo(device.titulo || "");
    } else if (device.exibicao) {
      setExibicaoOption('Personalizado');
      setTempTitulo("");
    } else {
      setExibicaoOption('Camera');
      setTempTitulo("");
    }

    setSelectedFile(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/gif'].includes(file.type)) {
      alert('Insira apenas imagens .png, .jpg e .gif.');
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      if (img.width !== 1920 || img.height !== 1080) {
        alert('Insira apenas imagens no tamanho 1920x1080.');
        URL.revokeObjectURL(img.src);
      } else {
        setSelectedFile(file);
      }
    };
  };

  const handleSaveEditDevice = async () => {
    if (editingDevice && user) {

      const expedientesArray = Object.values(editingDevice.expedientes);
      const temExpedientes = expedientesArray.length > 0;
      const expedientesValidos = expedientesArray.every(exp => exp.start && exp.end);
      const titulosValidos = Object.keys(editingDevice.expedientes).every(titulo => titulo.trim() !== "");

      if (!editingDevice.name.trim() || editingDevice.days.length === 0 || !temExpedientes || !expedientesValidos || !titulosValidos) {
        alert("Por favor, preencha o nome, selecione os dias e certifique-se de que TODOS os expedientes tenham título, início e fim.");
        return;
      }

      if (exibicaoOption === 'Pesquisa' && tempTitulo.trim() === "") {
        alert("Por favor, insira um título para a pesquisa de satisfação.");
        return;
      }

      const erroValidacao = validarExpedientes(editingDevice.expedientes);

      if (erroValidacao) {
        alert(erroValidacao);
        return;
      }

      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const currentDay = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"][now.getDay()];
      const originalDevice = devices.find(d => d.id === editingDevice.id);

      if (editingDevice.days.includes(currentDay)) {
        for (const [titulo, exp] of Object.entries(editingDevice.expedientes)) {
          const originalExp = originalDevice?.expedientes?.[titulo];

          if (originalExp) {
            const [origStartH, origStartM] = originalExp.start.split(':').map(Number);
            const origStartMinutes = origStartH * 60 + origStartM;

            if (originalExp.start !== exp.start && currentMinutes >= origStartMinutes) {
              alert(`O expediente "${titulo}" já iniciou hoje. Não é possível alterar o horário de início.`);
              return;
            }
          }

          // if (!originalExp || originalExp.end !== exp.end) {
          //   const [startH, startM] = exp.start.split(':').map(Number);
          //   const [endH, endM] = exp.end.split(':').map(Number);
          //   const startMinutes = startH * 60 + startM;
          //   const endMinutes = endH * 60 + endM;

          //   // Lógica de término considerando a madrugada
          //   const jaAcabou = (startMinutes < endMinutes)
          //     ? currentMinutes >= endMinutes
          //     : (currentMinutes >= endMinutes && currentMinutes < startMinutes);

          //   if (jaAcabou) {
          //     alert(`O horário de fim do expediente "${titulo}" já passou. Não é possível definir o fim para um horário no passado no dia de hoje.`);
          //     return;
          //   }
          // }
        }
      }

      const hojeStr = getHojeStr();

      if (originalDevice) {
        for (const [titulo, horario] of Object.entries(editingDevice.expedientes)) {
          const horarioOriginal = originalDevice.expedientes?.[titulo];

          if (horarioOriginal && (horarioOriginal.start !== horario.start || horarioOriginal.end !== horario.end)) {

            const relatorioHoje = relatorios.find(r =>
              r.dispositivoId === editingDevice.id &&
              r.Data === hojeStr &&
              r.expediente === titulo
            );

            if (relatorioHoje) {
              alert(`O expediente "${titulo}" já possui registros hoje. Não é mais possível alterar o seu horário hoje, tente novamente amanhã ou crie um novo expediente.`);
              return;
            }
          }
        }
      }

      setIsUploading(true);
      let finalExibicaoUrl = editingDevice.exibicao || "";

      try {
        if (exibicaoOption === 'Personalizado') {
          if (selectedFile) {
            finalExibicaoUrl = await uploadExibicaoImage(
              selectedFile,
              user.uid,
              editingDevice.id,
              editingDevice.exibicao
            );
          }
        } else if (exibicaoOption === 'Pesquisa') {
          finalExibicaoUrl = "Pesquisa";
        } else {
          finalExibicaoUrl = "";
          if (editingDevice.exibicao && editingDevice.exibicao !== "Pesquisa") {
            await uploadExibicaoImage(null, user.uid, editingDevice.id, editingDevice.exibicao);
          }
        }

        await editarDevice(editingDevice.id, {
          name: editingDevice.name,
          days: editingDevice.days,
          expedientes: editingDevice.expedientes,
          exibicao: finalExibicaoUrl,
          titulo: exibicaoOption === 'Pesquisa' ? tempTitulo : "",
          sobraLimpa: editingDevice.sobraLimpa,
        });

        setEditingDevice(null);
        setSelectedFile(null);

      } catch (error) {
        console.error("Erro ao editar dispositivo:", error);
        alert("Erro ao salvar alterações. Tente novamente!");
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleSelect = (id: string) => {
    setSelectedDeviceId(selectedDeviceId === id ? null : id);
  };

  const handleAddExpediente = (isEditing: boolean) => {
    const novoTitulo = ``;
    if (isEditing && editingDevice) {
      setEditingDevice({
        ...editingDevice,
        expedientes: { ...editingDevice.expedientes, [novoTitulo]: { start: '', end: '' } }
      });
    } else {
      setNewDevice({
        ...newDevice,
        expedientes: { ...newDevice.expedientes, [novoTitulo]: { start: '', end: '' } }
      });
    }
  };

  const handleExpedienteChange = (isEditing: boolean, antigoTitulo: string, novoValor: string, tipo: 'titulo' | 'start' | 'end') => {
    const setter = isEditing ? setEditingDevice : setNewDevice;

    setter((prev: any) => {
      const expedientesArray = Object.entries(prev.expedientes);
      const indexDoItem = expedientesArray.findIndex(([titulo]) => titulo === antigoTitulo);

      if (indexDoItem === -1) return prev;

      if (tipo === 'titulo') {
        expedientesArray[indexDoItem][0] = novoValor;
      } else {
        (expedientesArray[indexDoItem][1] as any)[tipo] = novoValor;
      }

      const expedientesAtualizados = Object.fromEntries(expedientesArray);

      return { ...prev, expedientes: expedientesAtualizados };
    });
  };

  const handleDeleteExpediente = (isEditing: boolean, titulo: string) => {
    const setter = isEditing ? setEditingDevice : setNewDevice;
    setter((prev: any) => {
      const expedientesAtualizados = { ...prev.expedientes };
      delete expedientesAtualizados[titulo];
      return { ...prev, expedientes: expedientesAtualizados };
    });
  };

  return (
    <Card extra="w-full p-4 h-full">
      <div className="mb-4 flex justify-between items-center">
        <h4 className="text-xl font-bold text-navy-700">
          Dispositivos
        </h4>
        <div className="flex gap-2">
          <button
            onClick={handleAddDevice}
            className="flex items-center gap-1 rounded bg-green-500 px-3 py-1 text-white hover:bg-green-600"
          >
            <MdAdd /> Adicionar
          </button>
          <button
            onClick={() => setDeleteMode(!deleteMode)}
            className={`flex items-center gap-1 rounded px-3 py-1 text-white ${deleteMode ? "bg-red-500 hover:bg-red-600" : "bg-gray-500 hover:bg-gray-600"
              }`}
          >
            <MdDeleteOutline /> {deleteMode ? "Cancelar" : "Apagar"}
          </button>
        </div>
      </div>

      <div className="overflow-y-auto max-h-[280px]">
        <table className="w-full text-left">
          <thead>
            <tr className="text-sm font-bold text-gray-600">
              <th>Nome</th>
              <th>Status</th>
              <th>Dias</th>
              <th>Horários</th>
              <th>Exibição</th>
              <th>Ação</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device) => (
              <tr
                key={device.id}
                onClick={() => handleSelect(device.id)}
                className={`cursor-pointer border-t ${selectedDeviceId === device.id ? "bg-gray-100" : ""
                  }`}
              >
                <td className="text-sm font-bold text-navy-700">
                  {device.name}
                </td>
                <td className="text-sm font-bold text-navy-700">
                  <div className="flex items-center gap-1">
                    {statusIcon(device.status)} {device.status}
                  </div>
                </td>
                <td className="text-sm font-bold text-navy-700">
                  {device.days.join(", ")}
                </td>
                <td className="py-2 text-sm font-bold text-navy-700">
                  {device.expedientes && Object.keys(device.expedientes).length > 0 ? (
                    Object.entries(device.expedientes)
                      .sort(([, horarioA], [, horarioB]) =>
                        horarioA.start.localeCompare(horarioB.start)
                      )
                      .map(([titulo, horario]) => (
                        <div key={titulo}>
                          {titulo}: {horario.start} - {horario.end}
                        </div>
                      ))
                  ) : (
                    <span> </span>
                  )}
                </td>
                <td className="py-2 text-sm font-bold text-navy-700">
                  {device.exibicao === 'Pesquisa' ? "Pesquisa" : device.exibicao ? "Conteúdo" : "Câmera"}
                </td>
                <td className="text-sm font-bold text-navy-700">
                  {deleteMode ? (
                    <button
                      className="text-sm text-red-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDevice(device.id);
                      }}
                    >
                      Apagar
                    </button>
                  ) : (
                    <button
                      className="text-sm text-blue-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditDevice(device);
                      }}
                    >
                      Editar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal: Adicionar */}
      {showAddModal && (
        <Modal onClose={() => setShowAddModal(false)}>
          <h2 className="text-lg font-bold mb-2">Novo Dispositivo</h2>
          <input
            className="w-full border rounded p-2 mb-2"
            placeholder="Nome do Dispositivo"
            value={newDevice.name}
            onChange={(e) => setNewDevice({ ...newDevice, name: e.target.value })}
          />
          <label className="block font-medium mb-1">Dias de Operação:</label>
          <div className="flex gap-2 mb-2">
            {["Seg", "Ter", "Qua", "Qui", "Sex", "Sab", "Dom"].map((day) => (
              <label key={day} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  className="defaultCheckbox relative flex h-[20px] min-h-[20px] w-[20px] min-w-[20px] appearance-none items-center 
      justify-center rounded-md border border-gray-300 text-white/0 outline-none transition duration-200
      checked:border-none checked:text-white hover:cursor-pointer checked:bg-[#CF4949]"
                  checked={newDevice.days.includes(day)}
                  onChange={() => {
                    setNewDevice((prev) => ({
                      ...prev,
                      days: prev.days.includes(day)
                        ? prev.days.filter((d) => d !== day)
                        : [...prev.days, day],
                    }));
                  }}
                />
                {day}
              </label>
            ))}
          </div>

          <label className="block font-medium mb-1">Expedientes:</label>
          {Object.entries(newDevice.expedientes).map(([titulo, horario], index) => (
            <div key={index} className="flex items-center gap-2 mb-2">
              <input
                type="text"
                className="w-full border rounded p-2 mb-2"
                placeholder="Título (ex: Almoço)"
                value={titulo.startsWith('Novo Expediente') ? '' : titulo}
                onChange={(e) => handleExpedienteChange(false, titulo, e.target.value, 'titulo')}
              />

              <input type="time" value={horario.start} onChange={(e) => handleExpedienteChange(false, titulo, e.target.value, 'start')} />
              <input type="time" value={horario.end} onChange={(e) => handleExpedienteChange(false, titulo, e.target.value, 'end')} />
            </div>
          ))}

          <button onClick={() => handleAddExpediente(false)} className="text-sm font-bold text-blue-500 mb-4">
            Adicionar Expediente +
          </button>

          <label className="block font-medium mb-1">Habilitar Sobra Limpa:</label>
          <div className="flex gap-4 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="newSobraLimpa"
                checked={newDevice.sobraLimpa === true}
                onChange={() => setNewDevice({ ...newDevice, sobraLimpa: true })}
                className="h-4 w-4 accent-[#CF4949]"
              />
              Sim
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="newSobraLimpa"
                checked={newDevice.sobraLimpa === false}
                onChange={() => setNewDevice({ ...newDevice, sobraLimpa: false })}
                className="h-4 w-4 accent-[#CF4949]"
              />
              Não
            </label>
          </div>

          <button
            className="mt-2 px-3 py-1 bg-[#CF4949] font-bold text-white transition duration-200 hover:bg-[#B84343] active:bg-[#B84343] rounded"
            onClick={handleSaveAndConfirm}
          >
            Gerar Token
          </button>
        </Modal>
      )}

      {/* Modal: Token */}
      {showTokenModal && (
        <Modal onClose={() => setShowTokenModal(false)} hideCloseButton>
          <h2 className="text-lg font-bold mb-2 text-center">Token Gerado</h2>
          <p className="text-4xl font-bold text-center text-green-500">{newDevice.token}</p>
          <p className="text-sm mb-4 text-center">Digite esse token nos dispositivos que deseja vincular.</p>
          <button
            className="mt-2 px-3 py-1 bg-[#CF4949] font-bold text-white transition duration-200 hover:bg-[#B84343] active:bg-[#B84343] rounded"
            onClick={() => setShowTokenModal(false)}
          >
            Feito
          </button>
        </Modal>
      )}

      {/* Modal: Editar */}
      {editingDevice && (
        <Modal onClose={() => setEditingDevice(null)}>
          <h2 className="text-lg font-bold mb-2">{editingDevice.name}</h2>
          <div className="w-full border rounded p-2 bg-gray-100 text-navy-700 font-medium overflow-hidden text-ellipsis whitespace-nowrap">
            <p className="flex items-center gap-1">Código Token: {editingDevice.token}</p>
          </div>
          {/* <p className="text-sm text-gray-600 mb-2">*Não é possível renomear dispositivos já criados</p> */}
          <label className="block font-medium mb-1">Dias de Operação:</label>
          <div className="flex gap-2 mb-2">
            {["Seg", "Ter", "Qua", "Qui", "Sex", "Sab", "Dom"].map((day) => (
              <label key={day} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  className="defaultCheckbox relative flex h-[20px] min-h-[20px] w-[20px] min-w-[20px] appearance-none items-center 
      justify-center rounded-md border border-gray-300 text-white/0 outline-none transition duration-200
      checked:border-none checked:text-white hover:cursor-pointer checked:bg-[#CF4949]"
                  checked={editingDevice.days.includes(day)}
                  onChange={() => {
                    setEditingDevice((prev) => ({
                      ...(prev as Device),
                      days: editingDevice.days.includes(day)
                        ? editingDevice.days.filter((d) => d !== day)
                        : [...editingDevice.days, day],
                    }));
                  }}
                />
                {day}
              </label>
            ))}
          </div>

          <label className="block font-medium mb-1">Expedientes:</label>
          {Object.entries(editingDevice.expedientes).map(([titulo, horario], index) => {
            const originalDevice = devices.find(d => d.id === editingDevice.id);
            const isExisting = originalDevice?.expedientes && originalDevice.expedientes[titulo];

            const hojeStr = getHojeStr();
            const temRelatorioHoje = relatorios.some(r =>
              r.dispositivoId === editingDevice.id &&
              r.Data === hojeStr &&
              r.expediente === titulo
            );

            const now = new Date();
            const currentMinutes = now.getHours() * 60 + now.getMinutes();
            const currentDay = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"][now.getDay()];
            const isOperatingToday = editingDevice.days.includes(currentDay);

            let emExecucao = false;
            let jaIniciouHoje = false;

            if (isOperatingToday && isExisting) {
              const originalHorario = originalDevice.expedientes[titulo];
              if (originalHorario.start && originalHorario.end) {
                const [startH, startM] = originalHorario.start.split(':').map(Number);
                const [endH, endM] = originalHorario.end.split(':').map(Number);
                const startMinutes = startH * 60 + startM;
                const endMinutes = endH * 60 + endM;

                if (startMinutes < endMinutes) {
                  // Diurno
                  emExecucao = currentMinutes >= startMinutes && currentMinutes <= endMinutes;
                  jaIniciouHoje = currentMinutes >= startMinutes;
                } else {
                  // Noturno (Madrugada)
                  emExecucao = currentMinutes >= startMinutes || currentMinutes <= endMinutes;
                  jaIniciouHoje = currentMinutes >= startMinutes || currentMinutes <= endMinutes;
                }
              }
            }

            const blockDelete = temRelatorioHoje || emExecucao;
            const blockStart = temRelatorioHoje || jaIniciouHoje;

            let deleteTitle = "Excluir";
            if (temRelatorioHoje) deleteTitle = "Não é possível excluir um expediente com registros hoje.";
            else if (emExecucao) deleteTitle = "Não é possível excluir um expediente em execução.";

            return (
              <div key={index} className="flex items-center gap-2 mb-2">
                {isExisting ? (
                  <div
                    className="w-full border rounded p-2 bg-gray-100 text-navy-700 font-medium overflow-hidden text-ellipsis whitespace-nowrap"
                    title={temRelatorioHoje ? "Este expediente já possui registros hoje e não pode ser alterado." : ""}
                  >
                    {titulo}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={titulo}
                    className="w-full border rounded p-2"
                    placeholder="Título do Expediente"
                    onChange={(e) => handleExpedienteChange(true, titulo, e.target.value, 'titulo')}
                  />
                )}

                <input
                  type="time"
                  value={horario.start}
                  disabled={blockStart}
                  className={blockStart ? "bg-gray-100 text-gray-400 cursor-not-allowed rounded border p-1" : "rounded border p-1"}
                  onChange={(e) => handleExpedienteChange(true, titulo, e.target.value, 'start')}
                  title={temRelatorioHoje ? "Não é possível alterar um expediente com registros hoje." : jaIniciouHoje ? "O expediente já iniciou hoje. Não é possível alterar o horário de início." : ""}
                />
                <input
                  type="time"
                  value={horario.end}
                  disabled={temRelatorioHoje}
                  className={temRelatorioHoje ? "bg-gray-100 text-gray-400 cursor-not-allowed rounded border p-1" : "rounded border p-1"}
                  onChange={(e) => handleExpedienteChange(true, titulo, e.target.value, 'end')}
                />

                <button
                  onClick={() => !blockDelete && handleDeleteExpediente(true, titulo)}
                  disabled={blockDelete}
                  className={blockDelete ? "text-gray-300 cursor-not-allowed" : "text-red-500"}
                  title={deleteTitle}
                >
                  <MdDeleteOutline size={20} />
                </button>
              </div>
            );
          })}

          <button onClick={() => handleAddExpediente(true)} className="text-sm font-bold text-blue-500 mb-4">
            Adicionar Expediente +
          </button>

          <label className="block font-medium mb-1">Opção de Exibição:</label>
          <div className="flex gap-4 mb-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="exibicaoOption"
                value="Camera"
                checked={exibicaoOption === 'Camera'}
                onChange={() => setExibicaoOption('Camera')}
                className="h-4 w-4 accent-[#CF4949]"
              />
              Padrão
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="exibicaoOption"
                value="Personalizado"
                checked={exibicaoOption === 'Personalizado'}
                onChange={() => setExibicaoOption('Personalizado')}
                className="h-4 w-4 accent-[#CF4949]"
              />
              Personalizado
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="exibicaoOption"
                value="Pesquisa"
                checked={exibicaoOption === 'Pesquisa'}
                onChange={() => setExibicaoOption('Pesquisa')}
                className="h-4 w-4 accent-[#CF4949]"
              />
              Pesquisa
            </label>
          </div>

          {exibicaoOption === 'Personalizado' && (
            <div className="mb-4">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept="image/png, image/jpeg, image/gif"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 border rounded p-2 text-gray-700 hover:bg-gray-100"
                disabled={isUploading}
              >
                <MdFileUpload />
                {selectedFile ? selectedFile.name : "Selecionar Arquivo (1920x1080)"}
              </button>
            </div>
          )}

          {exibicaoOption === 'Pesquisa' && (
            <div className="mb-4">
              <input
                type="text"
                className="w-full border rounded p-2"
                placeholder="Título (ex: Como está nosso serviço hoje?)"
                value={tempTitulo}
                onChange={(e) => setTempTitulo(e.target.value)}
              />
            </div>
          )}

          <label className="block font-medium mb-1 mt-2">Habilitar Sobra Limpa:</label>
          <div className="flex gap-4 mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="editSobraLimpa"
                checked={editingDevice.sobraLimpa === true}
                onChange={() => setEditingDevice({ ...editingDevice, sobraLimpa: true })}
                className="h-4 w-4 accent-[#CF4949]"
              />
              Sim
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="editSobraLimpa"
                checked={editingDevice.sobraLimpa === false}
                onChange={() => setEditingDevice({ ...editingDevice, sobraLimpa: false })}
                className="h-4 w-4 accent-[#CF4949]"
              />
              Não
            </label>
          </div>

          <button
            className="w-full mt-2 px-3 py-2 bg-[#CF4949] font-bold text-white transition duration-200 hover:bg-[#B84343] active:bg-[#B84343] rounded disabled:bg-gray-400"
            onClick={handleSaveEditDevice}
            disabled={isUploading}
          >
            {isUploading ? "Salvando..." : "Salvar Alterações"}
          </button>

        </Modal>
      )}
    </Card>
  );
}

